import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation, gql } from '@apollo/client'
import {
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  TextField,
  Box,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useSnackbar } from 'notistack'
import DeleteOutlineOutlined from 'components/material-symbol/icons/DeleteOutline'
import Dialog from 'components/dialog'

const useStyles = makeStyles(theme => ({
  titleRoot: {
    padding: theme.spacing(3, 7, 1, 3),
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#172024',
  },
  subtitle: {
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.75),
  },
  content: {
    padding: theme.spacing(1, 3, 2),
  },
  bodyText: {
    margin: 0,
    fontSize: '0.875rem',
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  formStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  actions: {
    padding: theme.spacing(2, 3, 3),
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
}))

const MAX_CUSTOM_REASON_LENGTH = 255

const DELETION_REASONS = [
  'No response',
  'Not accepting new students',
  'Not a fit for my coaching',
  'Cost',
  'Continued conversation outside app',
  'Already my student',
  'Referred to another coach',
  'Golfer is not in my area',
  'Other',
]

const deleteCoachingLead = gql`
  mutation DeleteCoachingLead($id: ID!, $deletionReason: String) {
    deleteCoachingLead(id: $id, deletionReason: $deletionReason) {
      message
      success
    }
  }
`

const DeleteLeadDialog = ({ lead, open, setOpen, refetch }) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const history = useHistory()

  const [deletionReason, setDeletionReason] = useState('')
  const [selectError, setSelectError] = useState(false)
  const [customReason, setCustomReason] = useState('')
  const [customReasonError, setCustomReasonError] = useState(false)

  const [deleteLead] = useMutation(deleteCoachingLead, {
    onCompleted: () => {
      enqueueSnackbar('Lead deleted successfully', {
        variant: 'success',
      })
      handleClose()
      refetch?.()
      history.push('/pga-coach/leads')
    },
    onError: () => {
      enqueueSnackbar('Error deleting lead', {
        variant: 'error',
      })
    },
  })

  const resetForm = () => {
    setDeletionReason('')
    setSelectError(false)
    setCustomReason('')
    setCustomReasonError(false)
  }

  const handleClose = () => {
    resetForm()
    setOpen(false)
  }

  const handleSelectChange = event => {
    if (selectError && event.target.value !== '') {
      setSelectError(false)
    }
    setDeletionReason(event.target.value)
  }

  const handleCustomChange = event => {
    setCustomReason(event.target.value)
    if (event.target.value.length > MAX_CUSTOM_REASON_LENGTH) {
      setCustomReasonError(true)
    } else {
      setCustomReasonError(false)
    }
  }

  const onDeleteLead = leadToDelete => {
    if (deletionReason === '') {
      setSelectError(true)
      return
    }
    if (customReasonError) {
      return
    }
    deleteLead({
      variables: {
        id: leadToDelete.id,
        deletionReason:
          deletionReason === 'Other' ? customReason : deletionReason,
      },
    })
  }

  const isDeleteButtonDisabled = () => {
    if (deletionReason === '') {
      return true
    }

    return (
      deletionReason === 'Other' &&
      (customReason == null || customReason === '')
    )
  }

  return (
    <Dialog
      isOpen={open}
      onClose={handleClose}
      showCloseButton
      fullWidth
      maxWidth="xs"
    >
      {() => (
        <>
          <DialogTitle disableTypography classes={{ root: classes.titleRoot }}>
            <Typography component="h2" className={classes.title}>
              Delete Lead
            </Typography>
            <Typography className={classes.subtitle}>
              This lead will be removed from your coach profile.
            </Typography>
          </DialogTitle>
          <DialogContent classes={{ root: classes.content }}>
            <Typography className={classes.bodyText}>
              Deleting a lead is an action that cannot be undone. Please provide
              a reason to help us improve both the coach and golfer experience.
            </Typography>
            <Box className={classes.formStack}>
              <FormControl
                required
                variant="outlined"
                error={selectError}
                fullWidth
              >
                <InputLabel>Select Reasoning</InputLabel>
                <Select
                  value={deletionReason}
                  onChange={handleSelectChange}
                  label="Select Reasoning *"
                  inputProps={{ 'data-testid': 'deletion-reason-select' }}
                >
                  {DELETION_REASONS.map((reason, index) => (
                    <MenuItem key={index} value={reason}>
                      {reason}
                    </MenuItem>
                  ))}
                </Select>
                {selectError ? (
                  <FormHelperText error>Please select a reason.</FormHelperText>
                ) : null}
              </FormControl>
              {deletionReason === 'Other' ? (
                <TextField
                  label="Please Explain Below *"
                  multiline
                  maxRows={4}
                  value={customReason}
                  onChange={handleCustomChange}
                  error={customReasonError}
                  helperText={
                    customReasonError
                      ? `Reason must be less than ${MAX_CUSTOM_REASON_LENGTH} characters.`
                      : null
                  }
                  inputProps={{ 'data-testid': 'custom-reason-input' }}
                  variant="outlined"
                  fullWidth
                />
              ) : null}
            </Box>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button color="primary" size="large" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="large"
              disableElevation
              startIcon={<DeleteOutlineOutlined fontSize="small" />}
              onClick={() => onDeleteLead(lead)}
              disabled={
                selectError || customReasonError || isDeleteButtonDisabled()
              }
            >
              Delete Lead
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default DeleteLeadDialog
